import React from 'react';

import { Button } from '@rbilabs/universal-components';
import { useBreakpointValue } from 'native-base';
import { GestureResponderEvent, StyleSheet, View } from 'react-native';

import { getDesignToken } from 'styles/constants';

import {
  ButtonWrapper,
  CardWrap,
  Description,
  Header,
  ImageLink,
  Terms,
  TextWrapper,
} from './styled';
import { CardProps } from './types';

/**
 * Displays a link comprised of an image and text, used for marketing
 */
const Card = ({
  image,
  header,
  description,
  callToAction,
  href,
  termsAccessibilityLabel,
  termsLinkText,
  termsLinkHref,
  marketingId,
  variant = 'default',
  bgColor,
  textColor,
  buttonBgColor,
  buttonTextColor,
  onPress,
  onPressTermsLink,
}: CardProps) => {
  const isSmallOrLarger = useBreakpointValue({ base: false, sm: true });

  const onPressMainLink = (event: GestureResponderEvent | undefined) =>
    onPress?.(event, { marketingId });

  const colorTweaks =
    variant === 'campaign'
      ? { color: textColor, bgColor }
      : {
          color: getDesignToken('text-default'),
          bgColor: getDesignToken('background-pattern'),
        };

  const buttonProps = {
    onPress: onPressMainLink,
    size: 'md',
    variant: variant === 'default' ? 'outline' : 'solid',
    ...(variant === 'campaign' && {
      bgColor: buttonBgColor,
      _text: {
        color: buttonTextColor,
      },
    }),
  } as const;

  return (
    <CardWrap
      {...colorTweaks}
      style={[styles.card, { flexBasis: isSmallOrLarger ? '49%' : '100%' }]}
    >
      <ImageLink linkPath={href} onPress={onPressMainLink}>
        {image}
      </ImageLink>
      <TextWrapper style={isSmallOrLarger ? styles.textWrapperDesktop : styles.textWrapperMobile}>
        <View>
          <Header {...colorTweaks} accessibilityLabel={header}>
            {header}
          </Header>
          <Description {...colorTweaks} accessibilityLabel={description}>
            {description}
          </Description>

          {termsLinkText && (
            <Terms
              {...colorTweaks}
              accessibilityRole="link"
              accessibilityLabel={termsAccessibilityLabel}
              linkPath={termsLinkHref || ''}
              onPress={(event: GestureResponderEvent | undefined) =>
                onPressTermsLink?.(event, { marketingId })
              }
            >
              {termsLinkText}
            </Terms>
          )}
        </View>
        <ButtonWrapper
          style={isSmallOrLarger ? styles.buttonWrapperDesktop : styles.buttonWrapperMobile}
        >
          <Button {...buttonProps}>{callToAction}</Button>
        </ButtonWrapper>
      </TextWrapper>
    </CardWrap>
  );
};

export default Card;

const styles = StyleSheet.create({
  card: {
    flexGrow: 0,
    flexShrink: 1,
  },
  buttonWrapperDesktop: {
    alignSelf: 'flex-end',
  },
  buttonWrapperMobile: {
    alignSelf: 'flex-start',
  },
  textWrapperDesktop: {
    padding: 20,
  },
  textWrapperMobile: {
    padding: 16,
  },
});
